import { annexes, C } from "@bd/content";
import { yearOf } from "./calendar";
import type { Company, GameState, Room } from "./state";
import { newId } from "./util";

export interface Placed {
  kind: string;
  x: number;
  y: number;
  rot: number;
}

/**
 * How a new room comes furnished: its centrepiece first, then workstations in rows with an aisle in front of
 * each (one person per workstation), then decor along the walls until about two thirds of the floor is used.
 * Pure and deterministic: the same room always gets the same layout.
 */
export function furnishLayout(type: string, w: number, h: number, door: { x: number; y: number } | undefined, week: number): Placed[] {
  const kit = C.room[type]?.kit;
  if (!kit) return [];
  const year = yearOf(week);
  const ok = (k: string) => !!C.furniture[k] && !((C.furniture[k].year ?? 0) > year);
  // 0 free, 1 furniture, 2 keep clear (aisles in front of desks, the doorway)
  const grid = new Uint8Array(w * h);
  const at = (x: number, y: number) => grid[y * w + x];
  const fits = (x: number, y: number, fw: number, fh: number) => {
    if (x < 0 || y < 0 || x + fw > w || y + fh > h) return false;
    for (let j = y; j < y + fh; j++) for (let i = x; i < x + fw; i++) if (at(i, j)) return false;
    return true;
  };
  const mark = (x: number, y: number, fw: number, fh: number, v: number) => {
    for (let j = y; j < y + fh; j++) for (let i = x; i < x + fw; i++) if (i >= 0 && j >= 0 && i < w && j < h && !grid[j * w + i]) grid[j * w + i] = v;
  };
  // the tile inside the doorway stays clear
  if (door) mark(Math.max(0, Math.min(w - 1, door.x)), Math.max(0, Math.min(h - 1, door.y)), 1, 1, 2);
  const out: Placed[] = [];
  const put = (kind: string, x: number, y: number) => {
    const k = C.furniture[kind];
    out.push({ kind, x, y, rot: 0 });
    mark(x, y, k.w, k.h, 1);
  };
  // centrepieces: as near the middle as they fit, leaving a ring to walk around
  const centre = (kind: string) => {
    const k = C.furniture[kind];
    const cx = Math.floor((w - k.w) / 2);
    const cy = Math.floor((h - k.h) / 2);
    let best: [number, number] | null = null;
    let bd = Infinity;
    for (let y = 0; y + k.h <= h; y++) for (let x = 0; x + k.w <= w; x++) {
      if (!fits(x, y, k.w, k.h)) continue;
      if (k.seat && y + k.h < h && !fits(x, y + k.h, k.w, 1)) continue;
      const d = Math.abs(x - cx) + Math.abs(y - cy);
      if (d < bd) { bd = d; best = [x, y]; }
    }
    if (best) {
      put(kind, best[0], best[1]);
      // whoever works it stands in front
      mark(best[0], best[1] + k.h, k.w, 1, 2);
    }
  };
  for (const kind of kit.feature.filter(ok)) centre(kind);
  // the room's signature piece gets the front corner before the desks fill the room: you can tell the room from across the studio
  for (const kind of (kit.signature ?? []).filter(ok)) {
    const k = C.furniture[kind];
    let best: [number, number] | null = null;
    let bv = -Infinity;
    for (let y = 0; y + k.h <= h; y++) for (let x = 0; x + k.w <= w; x++) {
      if (!fits(x, y, k.w, k.h) || x === 0) continue;
      const v = x + y * 2;
      if (v > bv) { bv = v; best = [x, y]; }
    }
    if (best) put(kind, best[0], best[1]);
  }
  // workstations: rows from the back wall, each with its aisle row in front
  const desks = kit.desks.filter(ok);
  let n = 0;
  if (desks.length) for (let y = 0; y + 1 < h; y += 2) {
    for (let x = 0; x < w; ) {
      const kind = desks[n % desks.length];
      const k = C.furniture[kind];
      if (fits(x, y, k.w, k.h) && fits(x, y + k.h, k.w, 1)) {
        put(kind, x, y);
        mark(x, y + k.h, k.w, 1, 2);
        n++;
        x += k.w;
      } else x++;
    }
  }
  // decor: back wall first, then the left wall, then anywhere free, until the room feels full
  const free = () => grid.reduce((s, v) => s + (v === 0 ? 1 : 0), 0);
  const target = Math.floor(w * h * 0.18);
  let placed = 0;
  const decor = kit.decor.filter(ok);
  const spots: [number, number][] = [];
  for (let x = 0; x < w; x++) spots.push([x, 0]);
  for (let y = 1; y < h; y++) spots.push([0, y]);
  for (let y = 1; y < h; y++) spots.push([w - 1, y]);
  for (let y = 1; y < h; y++) for (let x = 1; x < w - 1; x++) spots.push([x, y]);
  let i = 0;
  let tries = 0;
  while ((free() > target || placed < 2) && tries < decor.length * 2 && decor.length) {
    const kind = decor[i % decor.length];
    i++;
    tries++;
    const k = C.furniture[kind];
    if (k.floor) {
      // a rug goes in the open middle, under whatever stands on it later
      const x = Math.floor((w - k.w) / 2);
      const y = Math.max(0, h - k.h - 1);
      if (w >= k.w + 1 && h >= k.h + 1 && !out.some((p) => C.furniture[p.kind].floor)) out.push({ kind, x, y, rot: 0 });
      continue;
    }
    // a workstation among the decor still needs room in front for its person; tall pieces stand against a wall,
    // never out in the room where they'd hide the people at their desks
    const tall = TALL.has(kind);
    const s = spots.find(([x, y]) => fits(x, y, k.w, k.h) && (!k.seat || y + k.h >= h || fits(x, y + k.h, k.w, 1)) && (!tall || y === 0 || x === 0 || x + k.w === w));
    if (s) {
      put(kind, s[0], s[1]);
      if (k.seat) mark(s[0], s[1] + k.h, k.w, 1, 2);
      placed++;
      tries = 0;
    }
  }
  return out;
}

const TALL = new Set(["bookshelf", "shelf_rack", "games_shelf", "paint_shelf", "mini_shelf", "filing_cabinet", "fridge", "arcade", "plant_big", "tree", "coffee_machine", "water_cooler", "canvas_stack", "kiln", "print_rack", "trophy_shelf", "display_case"]);

/** One building on your lot: the main studio, or an annex. Tile coordinates are shared across the lot. */
export interface Plot {
  id: string;
  kind: string;
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
  rooms: string[];
  main: boolean;
}

/** Annexes stand in a row to the right of the main building, leaving it room to grow into the headquarters. */
export const ANNEX_GAP = 3;

/** Every building next door, bought or not, at its fixed spot on the street (left to right in content order). */
export function lots(co: Company): (Plot & { owned: boolean })[] {
  const main = floorSize(co);
  let x = Math.max(main.w, C.level.hq.tilesW) + ANNEX_GAP;
  const out: (Plot & { owned: boolean })[] = [];
  for (const t of annexes) {
    const own = (co.annexes ?? []).find((a) => a.kind === t.id);
    out.push({ id: own?.id ?? `lot:${t.id}`, kind: t.id, name: t.name, x, y: 0, w: t.tilesW, h: t.tilesH, rooms: t.rooms, main: false, owned: !!own });
    x += t.tilesW + ANNEX_GAP;
  }
  return out;
}

/** The buildings you own: the main studio, then each annex you've bought. */
export function plots(co: Company): Plot[] {
  const lvl = C.level[co.level];
  const main = floorSize(co);
  const out: Plot[] = [{ id: "main", kind: co.level, name: lvl.name, x: 0, y: 0, w: main.w, h: main.h, rooms: lvl.rooms, main: true }];
  for (const l of lots(co)) if (l.owned) {
    const { owned: _owned, ...plot } = l;
    out.push(plot);
  }
  return out;
}

/** The building a room sits in (fully inside), if any. */
export function plotFor(co: Company, r: { x: number; y: number; w: number; h: number }): Plot | undefined {
  return plots(co).find((p) => r.x >= p.x && r.y >= p.y && r.x + r.w <= p.x + p.w && r.y + r.h <= p.y + p.h);
}

/** The floor you build on: your building's size, or larger if an older save already has rooms beyond it. */
export function floorSize(co: Company): { w: number; h: number } {
  const lvl = C.level[co.level];
  let w = lvl.tilesW;
  let h = lvl.tilesH;
  // older saves had bigger buildings; rooms out in an annex don't count
  const annexX = C.level.hq.tilesW + ANNEX_GAP;
  for (const r of co.rooms) {
    if (r.x >= annexX && (co.annexes ?? []).length) continue;
    w = Math.max(w, r.x + r.w);
    h = Math.max(h, r.y + r.h);
  }
  return { w, h };
}

/** Workstations: one person each. */
export function deskCount(co: Company): number {
  let n = 0;
  for (const r of co.rooms) for (const f of r.furniture) if (C.furniture[f.kind]?.seat) n++;
  return n;
}

/** Furnish a room in place: new pieces go where nothing stands yet (used for new rooms and old saves). */
export function furnishRoom(state: GameState, room: Room): void {
  const door = room.door ? { x: room.door.x - room.x, y: room.door.y - room.y } : undefined;
  const taken = new Set<string>();
  for (const f of room.furniture) {
    const k = C.furniture[f.kind];
    if (!k || k.floor) continue;
    const fw = f.rot % 2 ? k.h : k.w;
    const fh = f.rot % 2 ? k.w : k.h;
    for (let j = f.y; j < f.y + fh; j++) for (let i = f.x; i < f.x + fw; i++) taken.add(`${i},${j}`);
  }
  const hasRug = room.furniture.some((f) => C.furniture[f.kind]?.floor);
  for (const p of furnishLayout(room.type, room.w, room.h, door, state.week)) {
    const k = C.furniture[p.kind];
    if (k.floor) {
      if (!hasRug) room.furniture.push({ id: newId(state, "f"), ...p });
      continue;
    }
    let clash = false;
    for (let j = p.y; j < p.y + k.h && !clash; j++) for (let i = p.x; i < p.x + k.w; i++) if (taken.has(`${i},${j}`)) clash = true;
    if (clash) continue;
    for (let j = p.y; j < p.y + k.h; j++) for (let i = p.x; i < p.x + k.w; i++) taken.add(`${i},${j}`);
    room.furniture.push({ id: newId(state, "f"), ...p });
  }
}
